import { useState, useRef } from 'react'
import { sanitizeHtml, processContent, r2 } from '@/lib/utils'
import { elementToMarkdown, copyRichContent } from '@/lib/clipboardContent'
import { showToast } from '@/components/layout/Toast'
import CostBreakdown from './CostBreakdown'
import TareaArtifacts from './TareaArtifacts'
import ArtifactViewer from './ArtifactViewer'
import type { CotizacionTarea, TareaArtifact } from '@/lib/types'
import { IconChevronDown, IconFileText, IconPackage, IconDollar, IconTimer, IconZap, IconCheck, IconPaperclip, IconCopy } from '@/components/icons/Icons'

interface Props {
  tarea: CotizacionTarea
  idx: number
  moneda: string
  isExpanded: boolean
  onToggle: () => void
  formatCurrency: (n: number, m: string) => string
}

export default function TareaDetalleInline({
  tarea, idx, moneda, isExpanded, onToggle, formatCurrency,
}: Props) {
  const [viewingArtifact, setViewingArtifact] = useState<TareaArtifact | null>(null)
  const [copied, setCopied] = useState<'md' | 'rich' | null>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  const tc = tarea.costos

  const copyAsMarkdown = async () => {
    const el = contentRef.current
    if (!el) return
    const md = elementToMarkdown(el)
    if (!md) return
    try {
      await navigator.clipboard.writeText(md)
      setCopied('md')
      showToast('Markdown copiado ✓', 'success')
      setTimeout(() => setCopied(null), 2000)
    } catch {
      showToast('No se pudo copiar', 'error')
    }
  }

  const copyForDocs = async () => {
    const el = contentRef.current
    if (!el) return
    try {
      const rich = await copyRichContent(el)
      setCopied('rich')
      showToast(rich ? 'Copiado con formato (Word/Docs) ✓' : 'Copiado como texto ✓', 'success')
      setTimeout(() => setCopied(null), 2000)
    } catch {
      showToast('No se pudo copiar', 'error')
    }
  }
  const totalMTok = (() => {
    if (tarea.costos_items?.length) {
      const total = tarea.costos_items
        .filter(c => c.unidad === 'MTok')
        .reduce((sum, c) => sum + c.cantidad, 0)
      return total > 0 ? r2(total) : null
    }
    if (!tc) return null
    const total = tc.tokens_input_mtok + tc.tokens_thinking_mtok + tc.tokens_output_mtok
    return total > 0 ? r2(total) : null
  })()

  return (
    <>
      {/* Clickable table row */}
      <tr
        className={`tarea-row-expandable ${isExpanded ? 'tarea-row-expandable--active' : ''}`}
        onClick={onToggle}
        role="button"
        tabIndex={0}
        onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggle() } }}
      >
        <td className="tarea-row__num">{idx + 1}</td>
        <td className="tarea-row__titulo">
          <strong>{tarea.titulo}</strong>
          {tarea.descripcion_corta && (
            <div className="tarea-row__desc">{tarea.descripcion_corta}</div>
          )}
        </td>
        <td className="tarea-row__horas">
          {tarea.horas_estimadas ? `${tarea.horas_estimadas}h` : '—'}
        </td>
        <td className="tarea-row__tokens">
          {totalMTok !== null ? `${totalMTok}M` : '—'}
        </td>
        <td className="tarea-row__monto">
          {formatCurrency(tarea.monto, moneda)}
        </td>
        <td className="tarea-row__entregables">
          {tarea.entregables.length > 0 && (
            <span className="tarea-row__entregables-count">{tarea.entregables.length}</span>
          )}
        </td>
        <td className="tarea-row__toggle">
          <span className={`tarea-row__chevron ${isExpanded ? 'tarea-row__chevron--open' : ''}`}>
            <IconChevronDown size={16} />
          </span>
        </td>
      </tr>

      {/* Expandable detail panel (inline, below the row) */}
      {isExpanded && (
        <tr className="tarea-detail-row">
          <td colSpan={7} className="tarea-detail-cell">
            <div className="tarea-detail-panel">
              {/* Header with task number + amount */}
              <div className="tarea-detail-panel__header">
                <div>
                  <span className="tarea-detail-panel__number">Tarea {idx + 1}</span>
                  <h3 className="tarea-detail-panel__title">{tarea.titulo}</h3>
                  <div className="tarea-detail-panel__badges">
                    {tarea.horas_estimadas && (
                      <span className="tarea-detail-panel__badge tarea-detail-panel__badge--horas">
                        <IconTimer size={12} /> {tarea.horas_estimadas}h estimadas
                      </span>
                    )}
                    {totalMTok !== null && (
                      <span className="tarea-detail-panel__badge tarea-detail-panel__badge--tokens">
                        <IconZap size={12} /> {totalMTok}M tokens
                      </span>
                    )}
                  </div>
                </div>
                <div className="tarea-detail-panel__monto">
                  {formatCurrency(tarea.monto, moneda)}
                </div>
              </div>

              {/* Content Detail (the artifact/HTML content) */}
              {tarea.contenido_detalle && (
                <div className="tarea-detail-panel__section">
                  <div className="tarea-detail-panel__section-head">
                    <h4 className="tarea-detail-panel__section-title"><IconFileText size={14} /> Especificación Técnica</h4>
                    <div className="tarea-detail-panel__copy-actions">
                      <button
                        type="button"
                        className="tarea-detail-panel__copy-btn"
                        onClick={copyAsMarkdown}
                        title="Copiar en formato Markdown (encabezados, tablas y listas)"
                      >
                        {copied === 'md' ? <IconCheck size={14} /> : <IconCopy size={14} />}
                        {copied === 'md' ? 'Copiado' : 'Markdown'}
                      </button>
                      <button
                        type="button"
                        className="tarea-detail-panel__copy-btn"
                        onClick={copyForDocs}
                        title="Copiar con formato para pegar en Word o Google Docs (con tablas)"
                      >
                        {copied === 'rich' ? <IconCheck size={14} /> : <IconCopy size={14} />}
                        {copied === 'rich' ? 'Copiado' : 'Word / Docs'}
                      </button>
                    </div>
                  </div>
                  <div
                    ref={contentRef}
                    className="tarea-detail-panel__content"
                    dangerouslySetInnerHTML={{ __html: sanitizeHtml(processContent(tarea.contenido_detalle)) }}
                  />
                </div>
              )}

              {/* Artifacts / Documentos Adjuntos */}
              {tarea.artifacts && tarea.artifacts.length > 0 && (
                <div className="tarea-detail-panel__section">
                  <h4 className="tarea-detail-panel__section-title"><IconPaperclip size={14} /> Documentos Adjuntos</h4>
                  <TareaArtifacts
                    artifacts={tarea.artifacts}
                    onViewArtifact={a => setViewingArtifact(a)}
                  />
                  {viewingArtifact && (
                    <ArtifactViewer
                      artifact={viewingArtifact}
                      onClose={() => setViewingArtifact(null)}
                    />
                  )}
                </div>
              )}

              {tarea.entregables.length > 0 && (
                <div className="tarea-detail-panel__section">
                  <h4 className="tarea-detail-panel__section-title"><IconPackage size={14} /> Entregables</h4>
                  <ul className="tarea-detail-panel__entregables">
                    {tarea.entregables.map((e, i) => (
                      <li key={i}>
                        <span className="tarea-entregable-check"><IconCheck size={12} /></span>
                        {e}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Cost Breakdown */}
              {tarea.costos && (
                <div className="tarea-detail-panel__section">
                  <h4 className="tarea-detail-panel__section-title"><IconDollar size={14} /> Desglose de Costos</h4>
                  <CostBreakdown costos={tarea.costos} moneda={moneda} />
                </div>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  )
}
